/**
 * ログイン画面（管理者向け・決定33・issue #297）。
 *
 * LINE の登録と同じ、電話番号＋認証番号の2段構えです。
 *
 *   手順1: 名簿に登録された電話番号を入れて「認証番号を送る」
 *   手順2: SMSで届いた6桁の認証番号を入れて「ログイン」
 *
 * 決めごと（docs/06 原則1〜3・決定22）:
 *  - 1画面に1つの手順だけを出す。いまどの手順かを見出しで言葉にする
 *  - 手順2では、送り先を下4桁で見せる（別の番号に送っていないかをご本人が確かめられるように）
 *  - 「もう一度送る」は送り直せるまでの秒数を言葉で出し、その間は押せない
 *  - 「電話番号を入れ直す」でいつでも手順1へ戻れる
 *  - 使えなくなった認証番号（期限切れ・一般の方など）のときは、手順1へ戻して理由を出す
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useSession, type LoginChallenge } from '../auth/session-context.js';
import { Button } from '../components/ui/Button.js';
import { FormCard } from '../components/ui/FormCard.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { buildAppName } from '../theme/build-brand.js';
import { LicensesLink } from './LicensesPage.js';

/**
 * 管理画面をお使いいただけない方（一般の方など）が入ろうとしたときの、次にしていただくこと。
 * 番号を確かめ直しても入れないので、別の言葉にする（総点検 J-09・M19）。
 */
const NOT_ADMIN_NEXT_STEP =
  'ふだんのやりとりは、スマホのアプリをお使いください。管理画面を使う必要があるときは、会社の管理者の方へご依頼ください。';

/** サーバの説明にアプリの案内がすでに入っているときの、次にしていただくこと（同じ文を2度出さない）。 */
const ASK_HQ_NEXT_STEP = '管理画面を使う必要があるときは、会社の管理者の方へご依頼ください。';

/** 認証番号が合わなかったときの、次にしていただくこと。 */
const CODE_NEXT_STEP =
  'SMSに届いた6桁の番号を確かめて、もう一度入れてください。届いていないときは「もう一度送る」を押してください。';

/** 全角の数字を半角にし、数字以外（空白・ハイフン）を取り除く。 */
function digitsOnly(text: string): string {
  return text
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
    .replace(/[^0-9]/g, '');
}

/** 送り先の見せ方（`*******0001` → 「0001」）。下4桁が読めなければ空。 */
function lastFour(hint: string): string {
  const digits = digitsOnly(hint);
  return digits.slice(-4);
}

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
}

/** 失敗を、画面に出す言葉に直す。 */
function failureBanner(caught: unknown, nextStep?: string): Banner {
  if (!(caught instanceof ApiError)) {
    return {
      tone: 'error',
      message: messageForStatus(0),
      nextStep: '通信の状態を確かめて、もう一度お試しください。',
    };
  }
  if (caught.status === 403) {
    return {
      tone: 'error',
      message: caught.message,
      nextStep: caught.message.includes('スマホのアプリ') ? ASK_HQ_NEXT_STEP : NOT_ADMIN_NEXT_STEP,
    };
  }
  if (caught.status === 0) {
    return {
      tone: 'error',
      message: caught.message,
      nextStep: '通信の状態を確かめて、もう一度お試しください。',
    };
  }
  // 番号の書き方・送り直しの間隔・回数の上限などは、サーバが日本語で理由を返す
  return {
    tone: 'error',
    message: caught.message,
    ...(nextStep !== undefined ? { nextStep } : {}),
  };
}

export function LoginPage(): ReactNode {
  const { status, notice, requestLoginCode, verifyLoginCode } = useSession();
  const builtName = buildAppName();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<LoginChallenge | null>(null);
  /** 「もう一度送る」を押せるようになる時刻 */
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [banner, setBanner] = useState<Banner | null>(null);
  const [busy, setBusy] = useState<'none' | 'send' | 'verify' | 'resend'>('none');

  // 送り直せるまでの秒数を1秒ごとに数え直す（待ち時間の間だけ）
  useEffect(() => {
    if (challenge === null || resendAt <= now) return;
    const timer = window.setTimeout(() => setNow(Date.now()), 1000);
    return () => window.clearTimeout(timer);
  }, [challenge, resendAt, now]);

  if (status === 'signed_in') return <Navigate to="/" replace />;

  function received(next: LoginChallenge): void {
    setChallenge(next);
    setCode('');
    const at = Date.now();
    setNow(at);
    setResendAt(at + next.resendAfter * 1000);
  }

  async function sendCode(): Promise<void> {
    if (digitsOnly(phone).length === 0) {
      setBanner({ tone: 'error', message: '電話番号を入力してください。' });
      return;
    }
    setBusy('send');
    setBanner(null);
    try {
      received(await requestLoginCode(phone));
    } catch (caught) {
      setBanner(failureBanner(caught, '電話番号を確かめて、もう一度お試しください。'));
    } finally {
      setBusy('none');
    }
  }

  async function resendCode(): Promise<void> {
    setBusy('resend');
    setBanner(null);
    try {
      received(await requestLoginCode(phone));
      setBanner({
        tone: 'success',
        message:
          '認証番号を送り直しました。新しく届いた番号を入れてください（前の番号は使えません）。',
      });
    } catch (caught) {
      setBanner(failureBanner(caught));
    } finally {
      setBusy('none');
    }
  }

  async function verify(): Promise<void> {
    if (!challenge) return;
    const digits = digitsOnly(code);
    if (digits.length !== 6) {
      setBanner({
        tone: 'error',
        message: '認証番号は6桁の数字で入れてください。',
        nextStep: 'SMSに届いた6桁の番号を、そのまま入れてください。',
      });
      return;
    }
    setBusy('verify');
    setBanner(null);
    try {
      await verifyLoginCode(challenge.challenge, digits);
    } catch (caught) {
      const unusable =
        caught instanceof ApiError &&
        (caught.status === 403 || caught.code === 'login_code_unusable');
      if (unusable) {
        // この認証番号ではもう入れない。電話番号の入力へ戻して理由を出す
        setChallenge(null);
        setCode('');
        setBanner(failureBanner(caught));
      } else {
        setBanner(failureBanner(caught, CODE_NEXT_STEP));
      }
    } finally {
      setBusy('none');
    }
  }

  function startOver(): void {
    setChallenge(null);
    setCode('');
    setBanner(null);
  }

  const waitSeconds = Math.max(0, Math.ceil((resendAt - now) / 1000));
  const footer = banner ? <ResultBanner {...banner} /> : undefined;

  return (
    <main className="admin-login">
      <div className="admin-login-brand">
        {/* 会社ごとの色。ログイン前は、この端末で前に読めた色 → 配る版に組み込んだ色 → 灰色 */}
        <span className="admin-header-mark" aria-hidden="true" />
        {/* 配る版に会社・アプリの名前を組み込んだときだけ、名前を添える（theme/build-brand.ts） */}
        {builtName ? <strong>{builtName}</strong> : null}
        <span>管理画面</span>
      </div>
      <h1>管理画面へのログイン</h1>
      {notice ? <ResultBanner tone="info" message={notice} /> : null}

      {challenge === null ? (
        <FormCard
          title="手順1／2　電話番号を入れる"
          description="登録されているあなたの電話番号へ、SMSで6桁の認証番号をお送りします。"
          submitLabel="認証番号を送る"
          submitBusyLabel="送っています…"
          busy={busy === 'send'}
          onSubmit={() => void sendCode()}
          footer={footer}
        >
          <label className="ui-field" htmlFor="login-phone">
            <span>電話番号</span>
            <input
              id="login-phone"
              name="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="例：09012345678"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </label>
          <small>ハイフン（-）は入れても入れなくてもかまいません。</small>
        </FormCard>
      ) : (
        <FormCard
          title="手順2／2　認証番号を入れる"
          description={
            lastFour(challenge.phoneHint) === ''
              ? '入力した電話番号へ、SMSで認証番号を送りました。'
              : `下4桁が「${lastFour(challenge.phoneHint)}」の電話番号へ、SMSで認証番号を送りました。`
          }
          submitLabel="ログイン"
          submitBusyLabel="ログインしています…"
          busy={busy === 'verify'}
          onSubmit={() => void verify()}
          footer={footer}
        >
          <label className="ui-field" htmlFor="login-code">
            <span>認証番号（6桁）</span>
            <input
              id="login-code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </label>
          <small>
            認証番号は、送ってから{Math.max(1, Math.round(challenge.expiresIn / 60))}
            分のあいだ使えます。
          </small>
        </FormCard>
      )}

      {challenge !== null ? (
        <div className="admin-login-more">
          <Button
            variant="secondary"
            busy={busy === 'resend'}
            busyLabel="送っています…"
            disabled={waitSeconds > 0 || busy !== 'none'}
            onClick={() => void resendCode()}
          >
            {waitSeconds > 0 ? `もう一度送る（あと${waitSeconds}秒）` : 'もう一度送る'}
          </Button>
          <Button variant="quiet" disabled={busy !== 'none'} onClick={startOver}>
            電話番号を入れ直す
          </Button>
        </div>
      ) : null}

      {/* 使っている部品のライセンス（issue #486） */}
      <LicensesLink />
    </main>
  );
}
